import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (next) navigate(next, { replace: true });
      else navigate(u.role === "volunteer" ? "/dashboard" : "/planner");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-900">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden bg-slate-950">
        <img
          src="/kelderke-logo.png"
          alt=""
          aria-hidden="true"
          className="absolute -right-16 -bottom-16 w-[28rem] h-[28rem] object-contain opacity-[0.04] pointer-events-none select-none"
        />
        <div className="relative flex items-center gap-3">
          <img
            src="/kelderke-logo.png"
            alt="'t Kelderke"
            className="w-12 h-12 object-contain"
          />
          <div>
            <p className="font-heading font-bold text-white text-lg">Bar 't Kelderke</p>
            <p className="text-sm text-slate-400">Goirle</p>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl font-extrabold text-white leading-tight">
            Vrijwilligers­planning<br />en beschikbaarheid
          </h1>
          <p className="text-slate-400 mt-4 max-w-md">
            Beschikbaarheid opgeven, diensten inplannen en eerlijk verdelen — alles op één
            plek, veilig en overzichtelijk.
          </p>
          <div className="flex items-center gap-2 mt-6 text-slate-500 text-sm">
            <ShieldCheck className="w-4 h-4 text-red-400" />
            Beveiligd volgens moderne privacy- en securityprincipes
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2.5 mb-2">
            <div className="w-11 h-11 rounded-lg bg-red-600 flex items-center justify-center p-1.5">
              <img src="/kelderke-logo.png" alt="'t Kelderke" className="w-full h-full object-contain" />
            </div>
            <p className="font-heading font-bold text-lg">Bar 't Kelderke</p>
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold">Inloggen</h2>
            <p className="text-sm text-muted-foreground mt-1">Welkom terug bij de planning.</p>
          </div>
          {error && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">E-mailadres</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" autoComplete="email" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Wachtwoord</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" autoComplete="current-password" />
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Inloggen
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            Geen account? Vraag een beheerder of planner om je uit te nodigen.
          </p>
        </form>
      </div>
    </div>
  );
}
